/**
 * Error Utilities for Billing Features
 *
 * Helpers for extracting user-friendly error messages from API responses.
 */

interface ApiErrorData {
  message?: string;
  error?: string;
  error_code?: string;
  /** FastAPI: either a plain string or a list of Pydantic validation errors. */
  detail?: string | unknown[];
  fieldList?: Array<{ path: string; message: string }>;
  success?: boolean;
}

/**
 * Extract a user-friendly error message from an RTK Query error response.
 *
 * Handles various error formats from the API:
 * - Standard { message: "..." } format
 * - Stedi errors with { error: "...", error_code: "..." }
 * - Validation errors with fieldList
 * - Generic fallback messages
 */
export function extractApiErrorMessage(
  error: unknown,
  fallbackMessage = 'An unexpected error occurred. Please try again.',
): string {
  if (!error) {
    return fallbackMessage;
  }

  // Log the error structure for debugging
  console.debug('[extractApiErrorMessage] Error object:', error);

  // Try to extract from various error structures
  const errorObj = error as Record<string, unknown>;

  // RTK Query error format: { status, data: { ... } }
  if ('data' in errorObj && errorObj.data) {
    const data = errorObj.data as ApiErrorData;
    const extracted = extractFromData(data);
    if (extracted) return extracted;
  }

  // Sometimes the error itself has the properties directly (not nested in data)
  if ('error' in errorObj || 'message' in errorObj || 'detail' in errorObj) {
    const extracted = extractFromData(errorObj as unknown as ApiErrorData);
    if (extracted) return extracted;
  }

  // RTK Query FetchBaseQueryError format: { status, error: string }
  if ('error' in errorObj && typeof errorObj.error === 'string') {
    return errorObj.error;
  }

  // Error with message property (standard Error)
  if ('message' in errorObj && typeof errorObj.message === 'string') {
    return errorObj.message;
  }

  // String error
  if (typeof error === 'string') {
    return error;
  }

  // Last resort: try to stringify the error
  try {
    const jsonStr = JSON.stringify(error);
    if (jsonStr !== '{}' && jsonStr !== '[]') {
      // If we can stringify it and it's not empty, include it for debugging
      console.error('[extractApiErrorMessage] Could not extract message from:', jsonStr);
      return `Error: ${jsonStr.substring(0, 200)}`;
    }
  } catch {
    // Can't stringify, fall through to fallback
  }

  return fallbackMessage;
}

/**
 * Extract error message from data object
 */
function extractFromData(data: ApiErrorData): string | null {
  if (!data) return null;

  // Check for field validation errors (like NPI validation)
  if (data.fieldList && Array.isArray(data.fieldList) && data.fieldList.length > 0) {
    const fieldErrors = data.fieldList
      .map(f => `${f.path}: ${f.message}`)
      .join('; ');
    return fieldErrors;
  }

  // Check for Stedi-specific error format: { error: "Failed to create provider in Stedi: Stedi API error: 400 - {...}" }
  if (data.error && typeof data.error === 'string') {
    // Try to parse nested Stedi error messages
    const stediMatch = data.error.match(/Stedi API error: \d+ - (.+)$/);
    if (stediMatch) {
      try {
        const nestedError = JSON.parse(stediMatch[1]) as ApiErrorData;
        if (nestedError.fieldList && nestedError.fieldList.length > 0) {
          return nestedError.fieldList
            .map(f => `${f.path}: ${f.message}`)
            .join('; ');
        }
        if (nestedError.message) {
          return nestedError.message;
        }
      } catch {
        // If parsing fails, that's okay - we'll return the raw error below
      }
    }
    // Return the error string directly
    return data.error;
  }

  // Standard message format
  if (data.message && typeof data.message === 'string') {
    return data.message;
  }

  // Detail format (FastAPI style)
  if (data.detail && typeof data.detail === 'string') {
    return data.detail;
  }

  // Array-shaped detail (FastAPI/Pydantic 422 validation errors):
  // [{ loc: ['body', 'line_items', 0, 'code_id'], msg: '...', type: '...' }]
  // Rendered as readable field-level messages instead of a JSON blob.
  if (Array.isArray(data.detail) && data.detail.length > 0) {
    const messages = data.detail
      .map(entry => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry === 'object') {
          const { loc, msg } = entry as { loc?: unknown; msg?: unknown };
          const message = typeof msg === 'string' ? msg : null;
          if (!message) return null;
          const path = Array.isArray(loc)
            ? loc
                .filter(part => part !== 'body')
                .map(String)
                .join('.')
            : '';
          return path ? `${path}: ${message}` : message;
        }
        return null;
      })
      .filter((message): message is string => Boolean(message));
    if (messages.length > 0) {
      return messages.join('; ');
    }
  }

  return null;
}

/**
 * Extract error code from API response if available
 */
export function extractApiErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  if ('data' in error) {
    const data = (error as { data: ApiErrorData }).data;
    return data?.error_code || null;
  }

  return null;
}

/**
 * Enrollment-specific error codes and their user-friendly messages
 */
const ENROLLMENT_ERROR_MESSAGES: Record<string, { message: string; navigateToStep?: number }> = {
  billing_entity_not_synced: {
    message: 'Please verify your billing identity with the clearinghouse first.',
    navigateToStep: 2, // Sync step in wizard
  },
  duplicate_enrollment: {
    message: 'You already have an enrollment with this payer.',
  },
  payer_not_routable: {
    message: "This payer doesn't support electronic claims through our clearinghouse.",
  },
  org_not_connected: {
    message: 'Please connect to the clearinghouse first.',
    navigateToStep: 0, // Connect step in wizard
  },
};

type SnackbarFn = (message: string, options?: { variant: 'error' | 'warning' | 'success' | 'info'; autoHideDuration?: number }) => void;

/**
 * Handle enrollment-specific API errors with appropriate user feedback
 *
 * @param error - The error from the API call
 * @param enqueueSnackbar - Notistack's snackbar function
 * @param onOpenWizard - Optional callback to open the wizard at a specific step
 */
export function handleEnrollmentError(
  error: unknown,
  enqueueSnackbar: SnackbarFn,
  onOpenWizard?: (step: number) => void,
): void {
  const errorCode = extractApiErrorCode(error);
  const enrollmentError = errorCode ? ENROLLMENT_ERROR_MESSAGES[errorCode] : null;

  if (enrollmentError) {
    enqueueSnackbar(enrollmentError.message, {
      variant: 'warning',
      autoHideDuration: 6000,
    });

    // Navigate to the appropriate wizard step if specified
    if (enrollmentError.navigateToStep !== undefined && onOpenWizard) {
      onOpenWizard(enrollmentError.navigateToStep);
    }
  } else {
    // Fallback to generic error extraction
    const message = extractApiErrorMessage(
      error,
      "We couldn't submit your enrollment request. Please try again.",
    );
    enqueueSnackbar(message, {
      variant: 'error',
      autoHideDuration: 10000,
    });
  }

  // Always log the error for debugging
  console.error('[handleEnrollmentError] Enrollment error:', error);
}
